import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminApi } from '@services/api';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { ErrorDisplay } from '@components/common/ErrorDisplay';

interface Organization {
  id: string;
  name: string;
  domain: string;
}

export default function InviteUser() {
  const [email, setEmail] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  
  // Fetch organizations
  const { data: orgsData, isLoading, error } = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: async () => {
      const response = await adminApi.listOrganizations();
      return response.data;
    },
  });
  
  const organizations: Organization[] = orgsData?.data?.organizations || [];
  
  // Invite mutation
  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; org_id: string }) => {
      const response = await adminApi.inviteUser(data);
      return response.data;
    },
    onSuccess: (data) => {
      setSuccessMessage(`Successfully invited ${data.data.email}!`);
      setEmail('');
      setSelectedOrgId('');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    },
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !selectedOrgId) {
      return;
    }
    
    inviteMutation.mutate({
      email,
      org_id: selectedOrgId,
    });
  };
  
  // Auto-select organization if only one exists
  useEffect(() => {
    if (organizations.length === 1 && !selectedOrgId) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId]);
  
  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <ErrorDisplay type="network" message="Failed to load organizations" onRetry={() => window.location.reload()} />
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Invite New User
        </h1>
        <p className="text-gray-600">
          Invite a new user to the Lite communication analysis platform.
        </p>
      </div>
      
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}
      
      {inviteMutation.isError && (
        <div className="mb-6">
          <ErrorDisplay type="validation" message={inviteMutation.error?.message || "Failed to send invitation"} />
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="user@example.com"
              disabled={inviteMutation.isPending}
            />
          </div>
          
          {/* Organization Select */}
          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
              Organization *
            </label>
            <select
              id="organization"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={inviteMutation.isPending}
            >
              <option value="">Select an organization...</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name} {org.domain && `(${org.domain})`}
                </option>
              ))}
            </select>
          </div>
          
          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={!email || !selectedOrgId || inviteMutation.isPending}
              className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {inviteMutation.isPending ? (
                <span className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Sending Invitation...
                </span>
              ) : (
                'Send Invitation'
              )}
            </button>
          </div>
        </div>
      </form>
      
      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-sm font-medium text-blue-900 mb-2">
          What happens next?
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• User account created in Stytch with <code className="bg-blue-100 px-1 rounded">onboarding</code> role</li>
          <li>• Welcome email sent with login instructions</li>
          <li>• User completes onboarding on first login</li>
          <li>• <code className="bg-blue-100 px-1 rounded">onboarding</code> role removed after completion</li>
        </ul>
      </div>
    </div>
  );
}

